import lod_ from "lodash";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import FormAction from "redux-react/actions/formAction";
import { useDispatch } from "react-redux";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { DateTime } from "luxon";
import MDButton from "components/Basics/MDButton";
import { t } from "i18next";
import Languages from "flatpickr/dist/l10n/index";
import MDDatePicker from "components/Basics/MDDatePicker";
import i18n from "i18n";

/**
 * Dialog to launch the scrap of reviews for a given resource
 * Allows to choose the date from which we want to scrap reviews
 * @param {boolean} open - If the dialog is open or not
 * @param {function} onClose - Function to call to close the dialog
 * @param {function} perform - Function to call to perform the action
 * @param {object} resource - The resource on which we want to scrap reviews
 * @param {object} action - The action to perform
 * @param {object|array} values - The values on which we want to perform the action
 * @returns {JSX.Element} - The ScrapReview component
 */
const ScrapReview = ({ open, onClose, perform, resource, action, values }) => {
	const dispatch = useDispatch();

	const [loading, setLoading] = useState(true);
	const [allRecentReviewsFetched, setAllRecentReviewsFetched] = useState(false);

	const [oldestReviewDate, setOldestReviewDate] = useState(null);

	const [scrapingDate, setScrapingDate] = useState(null);
	const DefaultLanguage = Languages[i18n.language] || Languages.en;

	const finishLoadingProcess = () => {
		setLoading(false);
		setAllRecentReviewsFetched(true);
	};

	/**
	 * Get the date of the last review fetched for this place
	 * to avoid fetching reviews we already have
	 * @returns {Promise<void>} - Promise that resolves when the operation is complete
	 */
	const getLastReviewDate = async () => {
		try {
			// 1- Get the google scraping channel
			const channel = await new Promise((resolve, reject) => {
				dispatch(
					FormAction.getItemsFromCollection(
						"channel",
						{
							query: {
								$or: [
									{ "config.shopResourceCode": resource.code },
									{ "config.siteResourceCode": resource.code }
								]
							},
							projection: { _id: 0, config: 1 }
						},
						res => {
							resolve(res.items[0]);
						}
					)
				);
			});

			if (lod_.isNil(channel)) {
				finishLoadingProcess();
				return;
			}

			// 2- Get the review resource's code
			const reviewResourceCode = lod_.get(channel, "config.reviewResourceCode", null);

			if (lod_.isNil(reviewResourceCode)) {
				finishLoadingProcess();
				return;
			}

			// 3- Get the review resource
			const reviewResource = await new Promise((resolve, reject) => {
				dispatch(
					FormAction.getItemsFromCollection(
						"resource",
						{
							query: { code: reviewResourceCode },
							projection: { _id: 0, storage: 1 }
						},
						res => {
							resolve(res.items[0]);
						}
					)
				);
			});

			if (lod_.isNil(reviewResource)) {
				finishLoadingProcess();
				return;
			}

			const reviewStorage = reviewResource?.storage?.collection;

			if (lod_.isNil(reviewStorage)) {
				finishLoadingProcess();
				return;
			}

			// 4- Query the oldest review
			const oldestReview = await new Promise((resolve, reject) => {
				dispatch(
					FormAction.getItemsFromCollection(
						reviewStorage,
						{
							query: {
								$or: [
									{
										$and: [{ googlePlaceCode: { $exists: true } }, { googlePlaceCode: values.code }]
									},
									{
										$and: [
											{ siteResourceCode: { $exists: true } },
											{
												siteResourceCode: values.code
											}
										]
									}
								]
							},
							sort: { createdAt: 1, createdDate: 1 },
							projection: { _id: 0, createdAt: 1, createdDate: 1 },
							limit: 1
						},
						res => {
							resolve(res.items[0]);
						}
					)
				);
			});

			const oldestReviewDateQuery = oldestReview?.createdAt || oldestReview?.createdDate || null;

			const newScrapingDate = DateTime.now().minus({ months: 1 }).toJSDate();

			if (lod_.isNil(oldestReviewDateQuery)) {
				/**
				 * If there is no review yet, we can fetch all the reviews within the last 2 months
				 */
				setAllRecentReviewsFetched(false);
				setScrapingDate(newScrapingDate);
				setOldestReviewDate(new Date());
			} else {
				const minimumDateTS = DateTime.now().minus({ months: 2 }).toJSDate().getTime();
				const parsedOldestReviewDate = DateTime.fromISO(oldestReviewDateQuery);
				const oldestReviewTS = parsedOldestReviewDate.toJSDate().getTime();

				if (minimumDateTS > oldestReviewTS) {
					/**
					 * If the oldest review is older than 2 months that means we
					 * have the historic of the last 2 months
					 */
					setAllRecentReviewsFetched(true);
				}

				if (oldestReviewTS < newScrapingDate.getTime()) {
					setScrapingDate(parsedOldestReviewDate.toJSDate());
				} else {
					setScrapingDate(newScrapingDate);
				}

				setOldestReviewDate(parsedOldestReviewDate.toJSDate());
			}

			setLoading(false);
		} catch {
			finishLoadingProcess();
		}
	};

	useEffect(() => {
		if (open === true) {
			setLoading(true);
			setAllRecentReviewsFetched(false);
			getLastReviewDate();
		}
	}, [open]);

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogTitle>{t("FORMS.RESOURCE.scrapReviewsTitle")}</DialogTitle>
			<DialogContent>
				<MDTypography variant="subtitle2" color="text">
					{t("FORMS.RESOURCE.scrapReviewsDescription")}
				</MDTypography>
				<MDBox mt={3}>
					{loading && (
						<MDBox display="flex" justifyContent="center" alignItems="center">
							<CircularProgress color="info" size={24} />
						</MDBox>
					)}
					{!loading && (
						<MDBox>
							{allRecentReviewsFetched && (
								<MDTypography variant="body2" color="text">
									{t("FORMS.RESOURCE.allReviewsScraped")}
								</MDTypography>
							)}
							{!allRecentReviewsFetched && (
								<MDDatePicker
									type="date"
									label={t("FORMS.RESOURCE.startingDate")}
									onChange={(dateObj, dateStr, instance) => {
										setScrapingDate(dateObj[0]);
									}}
									options={{
										time_24hr: true,
										dateFormat: "d M Y",
										locale: {
											...DefaultLanguage,
											firstDayOfWeek: 1
										},
										/**
										 * The max date is the oldest review date we have
										 * to avoid fetching reviews we already have
										 * The min date is 2 months ago
										 */
										maxDate: oldestReviewDate,
										minDate: DateTime.now().minus({ months: 2 }).toJSDate(),
										defaultDate: scrapingDate
									}}
									input={{
										label: t("FORMS.RESOURCE.startingDate"),
										placeholder: t("FORMS.RESOURCE.startingDate"),
										style: {
											width: "100%"
										}
									}}
									onDayCreate={(dObj, dStr, fp, dayElem) => {
										if (dayElem.dateObj.toDateString() === new Date().toDateString()) {
											dayElem.className += " today-date";
										}
									}}
								/>
							)}
						</MDBox>
					)}
				</MDBox>
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={onClose}>
					{t("SETTINGS.cancel")}
				</MDButton>
				<MDButton
					variant="contained"
					color="info"
					disabled={loading}
					onClick={() => {
						const copyAction = lod_.cloneDeep(action);
						lod_.set(copyAction, "config.fromDate", scrapingDate);
						lod_.set(copyAction, "config.toDate", oldestReviewDate);

						if (!Array.isArray(values)) {
							values = [values];
						}
						perform(copyAction, values);
						onClose();
					}}
				>
					{t("SETTINGS.confirm")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default ScrapReview;
